import type { Task } from '../../../lib/db/models'
import type { TaskEntryData } from '../components/TaskEntry'

/**
 * Per-Task-ref cache: same `Task` ref in → same `TaskEntryData` ref out.
 *
 * Why this matters: `TaskEntry` is wrapped in `React.memo`, and its `task`
 * prop is a `TaskEntryData` object. If every render builds a fresh
 * `TaskEntryData` from the same `Task`, memo comparison sees a new object
 * ref and re-renders the whole row — defeating the memo.
 *
 * With upstream `useStableEntityArray` + `buildMonthBuckets` / optimistic
 * override flatMaps, unchanged Tasks keep their object identity across
 * renders. Caching keyed on the `Task` ref lets `TaskEntry.memo` actually
 * bail for those rows.
 *
 * WeakMap keeps GC clean — entries vanish when the `Task` object is
 * collected.
 */
const entryDataCache = new WeakMap<Task, TaskEntryData>()

/**
 * Project a `Task` into the reduced `TaskEntryData` shape that `TaskEntry`
 * consumes. Result is cached per-Task-ref (see above).
 */
export function taskToEntryData(task: Task): TaskEntryData {
  const cached = entryDataCache.get(task)
  if (cached) return cached
  const data: TaskEntryData = {
    id: task.id,
    title: task.title,
    completed: task.status === 'completed',
    dormant: task.pausedAt != null,
    projectId: task.projectId,
    lifeSpace: task.lifeSpace,
    attention: task.attention,
    description: task.description,
    estimatedDuration: task.estimatedDuration,
    durationMode: task.durationMode,
    startDate: task.startDate,
    startTime: task.startTime,
    startPeriod: task.startPeriod,
    dueDate: task.dueDate,
    dueTime: task.dueTime,
    duePeriod: task.duePeriod,
    tags: task.tags,
  }
  entryDataCache.set(task, data)
  return data
}
